import { css, LitElement, html } from 'lit-element';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import { gm75, gm55, g300 } from '@aab/sc-styles-colors';
import { sy_others_blocked } from '@aab/sc-aab-icon-set';
import AabIcon from '@aab/sc-aab-icon/src/AabIcon';

var css_248z = css`*,
*::before,
*::after {
  box-sizing: border-box;
}

.text-center {
  text-align: center !important;
}

.d-flex {
  display: flex !important;
}

.m-auto {
  margin: auto !important;
}

.circle, .circle-sm {
  height: 32px;
  width: 32px;
  line-height: 32px;
  border-radius: 50%;
}

.circle.initials {
  font-family: "Roboto", "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
}

.circle.image {
  border: 1px solid #e9f0f0;
  background-color: #fff;
}

.circle.icon {
  background-color: #e9f0f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.circle-md {
  height: 40px;
  width: 40px;
  line-height: 40px;
  font-size: 17.5px !important;
}

.circle-lg {
  height: 48px;
  width: 48px;
  line-height: 48px;
  font-size: 21px !important;
}

.circle.icon [data-tag-name=aab-icon],
.circle-sm.icon [data-tag-name=aab-icon] {
  height: 24px;
  width: 24px;
}

.circle-md.icon [data-tag-name=aab-icon] {
  height: 30px;
  width: 30px;
}

.circle-lg.icon [data-tag-name=aab-icon] {
  height: 36px;
  width: 36px;
}

/* Colors below are the color variations from the design.
 * Make sure the number matches the number used in the code to compute the color.
 */
.color-0 {
  color: #732626;
  background-color: #F2D9D9;
}

.color-1 {
  color: #734026;
  background-color: #F2E1D9;
}

.color-2 {
  color: #735926;
  background-color: #F2EAD9;
}

.color-3 {
  color: #737326;
  background-color: #F2F2D9;
}

.color-4 {
  color: #597326;
  background-color: #EAF2D9;
}

.color-5 {
  color: #407326;
  background-color: #E1F2D9;
}

.color-6 {
  color: #194D19;
  background-color: #D9F2D9;
}

.color-7 {
  color: #267340;
  background-color: #D9F2E1;
}

.color-8 {
  color: #267359;
  background-color: #D9F2EA;
}

.color-9 {
  color: #267373;
  background-color: #D9F2F2;
}

.color-10 {
  color: #265973;
  background-color: #D9EAF2;
}

.color-11 {
  color: #264073;
  background-color: #D9E1F2;
}

.color-12 {
  color: #262673;
  background-color: #D9D9F2;
}

.color-13 {
  color: #402673;
  background-color: #E1D9F2;
}

.color-14 {
  color: #592673;
  background-color: #EAD9F2;
}

.color-15 {
  color: #732673;
  background-color: #F2D9F2;
}

.color-16 {
  color: #732659;
  background-color: #F2D9EA;
}

.color-17 {
  color: #732640;
  background-color: #F2D9E1;
}

.color-18 {
  color: #732626;
  background-color: #F2D9D9;
}

.color-19 {
  color: #734026;
  background-color: #F2E1D9;
}`;

class AabAvatar extends ScopedElementsMixin(LitElement) {
  constructor() {
    super();
    this.name = '';
    this.size = 'sm';
    // This number matches the number of colors defined in the CSS.
    this.numberOfColors = 20;
  }

  static get properties() {
    return {
      name: { type: String },
      icon: { type: String },
      image: { type: String },
      size: { type: String, converter: (value) => (AabAvatar.sizes.some((t) => t === value) ? value : 'sm') },
      // internal
      numberOfColors: { type: Number, attribute: false },
    };
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
    };
  }

  static get styles() {
    return [css_248z];
  }

  static get sizes() {
    return ['sm', 'md', 'lg'];
  }

  /** @function
   * @name getColorClassForName
   * This function calculates a 'semi random' color based on the given name.
   *  - For the same input the same output will be given.
   *    This guarantees that the same color will be shown for the same input throughout the application
   *  - For different input most likely (chance of 19 out of 20) a different output will be given.
   * @param {string} name
   * @returns {string} Returns the color code value
   */
  getColorClassForName(name) {
    // compute a short numeric hash based on the input.
    const hash = Array.from(name).reduce((acc, char) => {
      // eslint-disable-next-line no-bitwise
      const newHash = ((acc << 7) - acc) + char.charCodeAt(0);
      // eslint-disable-next-line no-bitwise
      return newHash & newHash;
    }, 0);
    // reduce the computed hash to a number between 0 and numberOfColors
    const colorNr = Math.abs(hash) % this.numberOfColors;
    return `color-${colorNr}`;
  }

  /** @function
   * @name getIconSize
   * This function returns the size of the icon
   * which is used to pass as an input to the size attribute of aab-icon component based on avatar size
   * @returns {number} Returns icon size
   */
  getIconSize() {
    let iconSize;
    if (this.size === 'lg') {
      iconSize = 4.5;
    } else if (this.size === 'md') {
      iconSize = 3.75;
    } else {
      iconSize = 3;
    }
    return iconSize;
  }

  /*
   * Renders the initials Avatar. Given the same name the color should be always the same see getColorClassForName().
   *  - input 1 word: first two letters.
   *  - input 2 or more words: first letter of first word + first letter of last word.
   */
  renderInitials() {
    const cleanName = this.name.trim().toUpperCase();
    const words = cleanName.split(' ');
    let letters;
    if (words.length > 1) {
      letters = `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`;
    } else {
      letters = words[0].substr(0, 2);
    }
    const color = this.getColorClassForName(cleanName);
    return html`<div class="circle initials text-center ${color} circle-${this.size}">${letters}</div>`;
  }

  renderImage() {
    const fallbackImage = sy_others_blocked
      .replace(/fill="#[0-9a-f]{6}"/ig, `fill="${gm75}"`)
      .replace('<svg ', `<svg style="background-color:${gm55}" `);
    return html`<img src="${this.image}" class="circle image circle-${this.size}" alt="${this.name}"
                     onerror="this.src='data:image/svg+xml;base64,${window.btoa(fallbackImage)}'">`;
  }

  renderIcon() {
    return html`
      <div class="circle circle-${this.size} text-center icon d-flex">
        <aab-icon class="m-auto" svg=${this.icon} size="${this.getIconSize()}" color=${g300}></aab-icon>
      </div>`;
  }

  render() {
    if (this.image && this.image.length) {
      return this.renderImage();
    }
    if (this.icon && this.icon.length) {
      return this.renderIcon();
    }
    return this.renderInitials();
  }
}

export { AabAvatar as default };
